import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Activity, Monitor, Search, Lightbulb } from 'lucide-react';
import Cookies from 'js-cookie';
import { useConfirm } from '../context/ConfirmContext';
import Header from './Header';
import Sidebar from './Sidebar';
import MainScreen from './MainScreen';
import LiveMonitorView from './LiveMonitorView';
import AllMasters from './AllMasters';
import QuickMessagesMaster from './QuickMessagesMaster';
import DepartmentMaster from './DepartmentMaster';
import WebsiteBlockMaster from './WebsiteBlockMaster';
import RoomMaster from './RoomMaster';
import PcInfo from './PcInfo';
import AppUsageReport from './AppUsageReport';
import IdleTimeReport from './IdleTimeReport';
import { API_BASE_URL } from '../config';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname.toLowerCase().includes('livemonitor') ? 'live-monitor' 
                   : location.pathname.toLowerCase().includes('pc-info') ? 'pc-info'
                   : location.pathname.toLowerCase().includes('masters/quick-messages') ? 'quick-messages'
                   : location.pathname.toLowerCase().includes('masters/departments') ? 'departments'
                   : location.pathname.toLowerCase().includes('masters/blocks') ? 'blocks'
                   : location.pathname.toLowerCase().includes('masters/rooms') ? 'rooms'
                   : location.pathname.toLowerCase().includes('masters') ? 'all-masters' 
                   : location.pathname.toLowerCase().includes('reports/app-usage') ? 'app-usage'
                   : location.pathname.toLowerCase().includes('reports/idle-time') ? 'idle-time'
                   : 'dashboard';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const confirm = useConfirm();
  
  const [licenseExceeded, setLicenseExceeded] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [activeDevicesList, setActiveDevicesList] = useState([]);
  const [isDeactivating, setIsDeactivating] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const checkLicense = async () => {
    try {
      const adminId = Cookies.get('adminId');
      const response = await fetch(`${API_BASE_URL}/admin/license-status?adminId=${adminId}`);
      if (response.ok) {
        const data = await response.json();
        setLicenseData(data);
        if (data.activeCount > data.licenseLimit) {
          setLicenseExceeded(true);
          const devRes = await fetch(`${API_BASE_URL}/devices?adminId=${adminId}`);
          if (devRes.ok) {
            const devices = await devRes.json();
            setActiveDevicesList(devices.filter(d => d.isActive !== false));
          }
        } else {
          setLicenseExceeded(false);
        }
      }
    } catch (err) {
      console.error('Error checking license status:', err);
    }
  };

  useEffect(() => {
    checkLicense();
    const interval = setInterval(checkLicense, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleDeactivateSingle = async (deviceId, deviceName) => {
    const isConfirmed = await confirm(`Are you sure you want to deactivate ${deviceName}?`);
    if (!isConfirmed) return;
    setIsDeactivating(prev => ({ ...prev, [deviceId]: true }));
    try {
      const adminId = Cookies.get('adminId') || '6a6b3b4b7c83176d2c5e991e';
      const response = await fetch(`${API_BASE_URL}/admin/deactivate-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId, deviceId })
      });
      if (response.ok) {
        await checkLicense();
      } else {
        alert(`Failed to deactivate ${deviceName}.`);
      }
    } catch (err) {
      console.error('Error deactivating device:', err);
      alert('Error occurred.');
    } finally {
      setIsDeactivating(prev => ({ ...prev, [deviceId]: false }));
    }
  };

  const setActiveView = (view) => {
    if (view === 'dashboard') {
      navigate('/dashboard');
    } else if (view === 'all-masters') {
      navigate('/masters');
    } else if (view === 'pc-info') {
      navigate('/pc-info');
    } else {
      navigate('/LiveMonitor');
    }
  };

  return (
    <div 
      className={`app-layout ${isCollapsed ? 'collapsed' : ''}`}
      style={licenseExceeded ? { gridTemplateColumns: '1fr', gridTemplateAreas: '"header" "main"' } : {}}
    >
      {!licenseExceeded && (
        <Sidebar 
          activeView={['quick-messages', 'departments', 'blocks'].includes(activeView) ? 'all-masters' : activeView} 
          setActiveView={(v) => { setActiveView(v); setIsMobileOpen(false); }} 
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
        />
      )}
      <Header toggleMobileMenu={() => setIsMobileOpen(true)} />
          {licenseExceeded ? (
            <div className="main-content" style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto', backgroundColor: 'var(--bg-main)', fontFamily: 'var(--app-font-family)', boxSizing: 'border-box' }}>
              {/* Top Banner */}
            <div style={{ 
              background: 'var(--bg-card)', 
              border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', 
              borderRadius: '20px', 
              width: '100%', 
              maxWidth: '1000px', 
              padding: '48px 40px', 
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              marginBottom: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                border: '1px solid var(--accent-primary)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 24px',
                backgroundColor: 'rgba(var(--app-primary-color-rgb), 0.08)'
              }}>
                <AlertTriangle size={32} color="var(--accent-primary)" />
              </div>
              
              <h1 style={{ fontSize: '28px', fontWeight: '800', margin: '0 0 16px 0', letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>Plan Downgraded</h1>
              <p style={{ fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 8px 0', fontWeight: '500' }}>
                Your active device count exceeds your current plan limit.
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                Your current plan supports <strong style={{ color: 'var(--accent-primary)' }}>{licenseData?.licenseLimit} devices</strong>, but you have <strong style={{ color: 'var(--text-primary)' }}>{licenseData?.activeCount} active devices</strong>
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--accent-primary)', fontSize: '13px', fontWeight: '600', marginBottom: '32px' }}>
                <Lightbulb size={16} />
                <span>Please deactivate {licenseData?.activeCount - licenseData?.licenseLimit} workstation{(licenseData?.activeCount - licenseData?.licenseLimit) > 1 ? 's' : ''} below to restore full service.</span>
              </div>
              
              {/* Progress Bar Container */}
              <div style={{ background: 'var(--bg-main)', borderRadius: '16px', padding: '20px 24px', width: '100%', maxWidth: '440px', margin: '0 auto', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>DEVICE LICENSE ALLOCATION</span>
                  <span style={{ color: 'var(--accent-primary)' }}>{licenseData?.activeCount} / {licenseData?.licenseLimit} DEVICES</span>
                </div>
                <div style={{ height: '6px', background: 'var(--border-light)', borderRadius: '3px', overflow: 'hidden', marginBottom: '16px' }}>
                  <div style={{ height: '100%', width: '100%', background: 'var(--accent-primary)', borderRadius: '3px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: 'var(--accent-primary)', fontSize: '12px', fontWeight: '600' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-primary)' }}></div>
                  <span>Over limit by {licenseData?.activeCount - licenseData?.licenseLimit} device{(licenseData?.activeCount - licenseData?.licenseLimit) > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div style={{ width: '100%', maxWidth: '1000px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h2 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', margin: '0 0 6px 0', letterSpacing: '1px' }}>ACTIVE WORKSTATIONS</h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>Showing all devices matching your search</p>
                </div>
                <div style={{ position: 'relative', width: '280px' }}>
                  <Search size={16} color="var(--text-secondary)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input 
                    type="text" 
                    placeholder="Search by name, user..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ 
                      width: '100%', 
                      background: 'var(--input-bg)', 
                      border: '1px solid var(--border-light)', 
                      borderRadius: '20px', 
                      padding: '10px 10px 10px 36px', 
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }} 
                  />
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: '16px' 
              }}>
                {activeDevicesList
                  .filter(device => 
                    device.computerName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                    device.assignedUser?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(device => (
                    <div key={device._id} style={{ 
                      background: 'var(--bg-card)', 
                      border: '1px solid rgba(var(--app-primary-color-rgb), 0.15)', 
                      borderRadius: '12px', 
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            background: 'rgba(var(--app-primary-color-rgb), 0.12)', 
                            border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', 
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Monitor size={20} color="var(--accent-primary)" />
                          </div>
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {device.computerName}
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {device.assignedUser || 'Unassigned'}
                            </div>
                          </div>
                        </div>
                        <div style={{ 
                          background: 'rgba(34, 197, 94, 0.1)', 
                          color: '#22c55e', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '10px', 
                          fontWeight: '700', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          letterSpacing: '0.5px'
                        }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }}></div>
                          ACTIVE
                        </div>
                      </div>
                      
                      <button 
                        onClick={() => handleDeactivateSingle(device._id, device.computerName)}
                        disabled={isDeactivating[device._id]}
                        style={{ 
                          width: '100%',
                          background: 'rgba(220, 38, 38, 0.05)', 
                          border: '1px solid rgba(220, 38, 38, 0.4)', 
                          color: '#ef4444', 
                          padding: '10px', 
                          borderRadius: '8px', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          cursor: isDeactivating[device.deviceId] ? 'not-allowed' : 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: '8px', 
                          opacity: isDeactivating[device._id] ? 0.5 : 1, 
                          transition: 'all 0.2s' 
                        }}
                        onMouseOver={e => !isDeactivating[device._id] && (e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)')}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.05)'}
                      >
                        <Activity size={16} />
                        {isDeactivating[device._id] ? 'Deactivating...' : 'Deactivate Device'}
                      </button>
                    </div>
                ))}
              </div>
            </div>
          </div>
          ) : (
            <>
              {activeView === 'dashboard' && <MainScreen />}
              {activeView === 'live-monitor' && <LiveMonitorView />}
              {activeView === 'pc-info' && <PcInfo />}
              {activeView === 'all-masters' && <AllMasters />}
              {activeView === 'quick-messages' && <QuickMessagesMaster />}
              {activeView === 'departments' && <DepartmentMaster />}
              {activeView === 'blocks' && <WebsiteBlockMaster />}
              {activeView === 'rooms' && <RoomMaster />}
              {activeView === 'app-usage' && <AppUsageReport />}
              {activeView === 'idle-time' && <IdleTimeReport />}
            </>
          )}
    </div>
  );
};

export default Layout;
